// What a lead did after signing up, from the appointments attributed to their sign-ups (last-touch booking
// credit) and their counter-sign uses since signing up. One kind per person, the best outcome winning:
// came in > used a perk at the counter > booked > the latest no-show / cancel.
// ---------------------------------------------------------------------------

export const LEAD_RESULT_KINDS = ['NOT_BOOKED', 'BOOKED', 'CAME_IN', 'USED_AT_COUNTER', 'NO_SHOW', 'CANCELLED'] as const;
export type LeadResultKind = (typeof LEAD_RESULT_KINDS)[number];

export const LEAD_RESULT_LABELS: Record<LeadResultKind, string> = {
  NOT_BOOKED: 'Not booked',
  BOOKED: 'Booked',
  CAME_IN: 'Came in',
  USED_AT_COUNTER: 'Came in at the counter',
  NO_SHOW: 'No-show',
  CANCELLED: 'Cancelled',
};

export interface LeadResultAppointment {
  status: string;
  startTime: Date;
  /** Net revenue of a COMPLETED visit; null when unknown or hidden from the viewer. */
  revenueCents: number | null;
  /**
   * A COMPLETED visit proves they came (false = a quiet Auto-Done while a Done spends no perks: time passed, nobody
   * saw them, so it reads as the booking it was). Omitted = proven.
   */
  proven?: boolean;
}

export interface DerivedLeadResult {
  kind: LeadResultKind;
  /** The appointment's start time (the counter use for USED_AT_COUNTER) behind the kind; null for NOT_BOOKED. */
  at: Date | null;
  /** Total net revenue of the completed visits (CAME_IN only); null when none is known. */
  revenueCents: number | null;
}

const ENDED_WITHOUT_VISIT = new Set(['CANCELLED', 'NO_SHOW']);

/**
 * Pure: the person's result from their attributed (non-deleted) appointments and their latest counter-sign use
 * since signing up (`counterUseAt`, null = none).
 * - Any proven COMPLETED → CAME_IN at the latest one, revenue summed over them.
 * - Else a counter use → USED_AT_COUNTER at that use (Hayven can't see a booking on the salon's POS).
 * - Else any live booking (not cancelled / no-show; an unproven completed one included) → BOOKED at the latest one.
 * - Else the latest cancelled / no-show appointment names the kind; none at all → NOT_BOOKED.
 */
export function deriveLeadResult(
  appointments: readonly LeadResultAppointment[],
  counterUseAt: Date | null = null
): DerivedLeadResult {
  const latest = (rows: readonly LeadResultAppointment[]) =>
    rows.reduce<LeadResultAppointment | null>((best, row) => (!best || row.startTime > best.startTime ? row : best), null);

  const completed = appointments.filter((row) => row.status === 'COMPLETED' && row.proven !== false);
  if (completed.length > 0) {
    const known = completed.filter((row) => row.revenueCents !== null);
    return {
      kind: 'CAME_IN',
      at: latest(completed)!.startTime,
      revenueCents: known.length > 0 ? known.reduce((sum, row) => sum + (row.revenueCents ?? 0), 0) : null,
    };
  }
  if (counterUseAt) return { kind: 'USED_AT_COUNTER', at: counterUseAt, revenueCents: null };
  const live = latest(appointments.filter((row) => !ENDED_WITHOUT_VISIT.has(row.status)));
  if (live) return { kind: 'BOOKED', at: live.startTime, revenueCents: null };
  const ended = latest(appointments);
  if (ended) return { kind: ended.status === 'NO_SHOW' ? 'NO_SHOW' : 'CANCELLED', at: ended.startTime, revenueCents: null };
  return { kind: 'NOT_BOOKED', at: null, revenueCents: null };
}

/**
 * The Leads "Result" filter (URL `?result=`): the person's outcome as the KPIs count it.
 * - `needs_follow_up` = opted in, and no visit booked since signing up nor one still ahead, and no perk used at the
 *   counter since then (never booked, no-show and cancelled people alike) — the Needs follow-up card.
 * - `booked` = a campaign-attributed appointment that is not cancelled / a no-show (came in included), the
 *   Booked card; `came_in` = a proven completed one, or a perk used at the counter since signing up.
 * - `not_booked` = no attributed appointment and did not come in; `no_show_cancelled` = attributed appointments,
 *   every one cancelled or a no-show, and did not come in.
 */
export const LEAD_RESULT_FILTERS = ['needs_follow_up', 'not_booked', 'booked', 'came_in', 'no_show_cancelled'] as const;
export type LeadResultFilter = (typeof LEAD_RESULT_FILTERS)[number];

export const LEAD_RESULT_FILTER_LABELS: Record<LeadResultFilter, string> = {
  needs_follow_up: 'Needs follow-up',
  not_booked: 'Not booked',
  booked: 'Booked',
  came_in: 'Came in',
  no_show_cancelled: 'No-show or cancelled',
};

/**
 * The Result filters about bookings in Hayven (Calendar on). Without the Calendar they can only hold bookings from
 * before the salon switched, which never change again (each row still reads "Booked …" / "No-show …"), so they drop.
 * The New Client Offer guest chips keep Booked / No-show while guests hold them instead: an open offer still moves on.
 */
const LEAD_RESULT_FILTERS_NEEDING_BOOKINGS: readonly LeadResultFilter[] = ['booked', 'no_show_cancelled'];

/**
 * Where the bookings live outside Hayven (a salon's own POS) nobody "isn't booked yet" as far as Hayven can tell: the
 * person simply has not come in yet (no Hayven Tap use, and no visit or sale Hayven sees). A reply does not move them
 * out of the queue. The same word the New Client Offer guests list uses.
 */
export const LEAD_RESULT_NOT_IN_YET_LABEL = 'Not in yet';

/** The Result filters a salon can pick: without bookings in Hayven, Booked and No-show or cancelled drop out. */
export function leadResultFiltersFor(bookingsVisible: boolean): readonly LeadResultFilter[] {
  return bookingsVisible ? LEAD_RESULT_FILTERS : LEAD_RESULT_FILTERS.filter((filter) => !LEAD_RESULT_FILTERS_NEEDING_BOOKINGS.includes(filter));
}

/** A Result filter's label for this salon ("Not booked" reads "Not in yet" without bookings in Hayven). */
export function leadResultFilterLabel(filter: LeadResultFilter, bookingsVisible: boolean): string {
  return !bookingsVisible && filter === 'not_booked' ? LEAD_RESULT_NOT_IN_YET_LABEL : LEAD_RESULT_FILTER_LABELS[filter];
}

/** A result kind's label for this salon (NOT_BOOKED reads "Not in yet" without bookings in Hayven). */
export function leadResultLabel(kind: LeadResultKind, bookingsVisible: boolean): string {
  return !bookingsVisible && kind === 'NOT_BOOKED' ? LEAD_RESULT_NOT_IN_YET_LABEL : LEAD_RESULT_LABELS[kind];
}

/** A saved or deep-linked Result filter this salon cannot use falls back to "all results" (empty). */
export function usableLeadResultFilter(filter: LeadResultFilter | undefined, bookingsVisible: boolean): LeadResultFilter | undefined {
  return filter && leadResultFiltersFor(bookingsVisible).includes(filter) ? filter : undefined;
}
